/**
 * The ONE temporal-literal policy for filter SQL, shared by the emitters that
 * must agree byte for byte (like `sqlBooleanLiteral`): the renderer's
 * `filterWhereBuilder.ts` (Visual Query Editor chips — WHERE and HAVING
 * `MIN`/`MAX`; Data Profile) and the Table Explorer generators'
 * `queryUtils.ts`. It covers every operator that compares the column with a
 * value — `=`, `!=`, `<`, `<=`, `>`, `>=`, `BETWEEN`, a plain-column `lookup`
 * and `enum` (scalar and each `IN (…)` element). It applies only when the
 * caller knows the compared column's data type is a date / datetime /
 * timestamp: a date-shaped string compared with a TEXT column must stay a
 * plain string literal.
 *
 * Values arrive as the filter's date input writes them — `YYYY-MM-DDThh:mm`
 * (the browser drops zero seconds) or `…:ss` — as a date-only `YYYY-MM-DD`, as
 * a user-written literal read back from SQL (any zone), or as a grid cell's /
 * lookup row's / enum cache's UTC-anchored ISO instant (`…T04:34:33.000Z`, or
 * a JS `Date`). Every engine's tz-naive read-back is UTC-anchored
 * (Docs/DbEngineLogic.md), so for a tz-naive column that text IS the stored
 * wall clock.
 *
 * The value is rewritten ONLY where the engine cannot take it as written
 * (round 4 — the input no longer appends seconds for every engine):
 *
 * - Oracle — no ISO-8601 literal: a bare string is converted with the
 *   session's NLS_DATE_FORMAT (`DD-MON-RR` by default → ORA-01861). `DATE` →
 *   `TO_DATE(…, 'YYYY-MM-DD HH24:MI:SS')` (keeps an index on the DATE column
 *   usable, unlike a TIMESTAMP literal); `TIMESTAMP…` → `TO_TIMESTAMP(…,
 *   '… HH24:MI:SS.FF')`; a zoned value on a `WITH [LOCAL] TIME ZONE` column →
 *   `TO_TIMESTAMP_TZ` with its offset. Same masks as the clone / blueprint
 *   emitters (`cloneUtils.formatTemporalLiteral`).
 * - SQL Server — `'YYYY-MM-DDThh:mm:ss[.fff][±hh:mm]'`. Measured on SQL
 *   Server 2025: `'2024-03-01T00:00'` (no seconds) is "Conversion failed" for
 *   every date type; the `T` form is the only one independent of SET
 *   LANGUAGE / DATEFORMAT (`'2024-03-01'` reads as 3 January on `datetime`
 *   under `british`); `+0200` / `+02` are rejected (only `±hh:mm` parses);
 *   `datetime` / `smalldatetime` reject any offset while `datetime2` / `date`
 *   silently ignore it — so the zone is kept only on `datetimeoffset` (a
 *   `Z` parses everywhere).
 * - PostgreSQL — `undefined` (verbatim): PG parses the date input's ISO text
 *   (whole minutes, `T`, any zone) for date / timestamp / timestamptz, and a
 *   tz-naive column ignores the zone itself.
 * - MySQL — `undefined` (verbatim): MySQL parses whole minutes and the `T`,
 *   and 8.0.19+ HONOURS an explicit offset (converted to the session time
 *   zone) — stripping it compared a different instant (round-4 #10). A
 *   trailing `Z` is ignored with warning 1292 and compares the same rows as
 *   the zone-less text (measured on 8.0.46 and 9.5.0), so there is no reason
 *   to rewrite that either.
 * - SQLite — `undefined` (verbatim): no temporal type; values compare as text
 *   in whatever format the column stores (round-4 #9: `'2024-03-01T00:00'`
 *   must still match itself).
 * - Kusto / mock — `undefined`: Kusto builds KQL in `kustoFilterMapping.ts`.
 *
 * `TIME` (time-of-day) columns are out of scope (`undefined`): the filter
 * input is a date-and-time picker there too.
 */

import type { DatabaseEngineId } from '../engines/engineTypes';

type TemporalKind = 'date' | 'timestamp' | 'timestamptz';

type ParsedTemporal = {
  readonly date: string;
  readonly time: string;
  /** Fraction digits without the dot, or ''. */
  readonly fraction: string;
  /** `Z` / `+hh:mm` / `-hh:mm`, or '' for a zone-less value. */
  readonly zone: string;
};

const TEMPORAL_VALUE_RE =
  /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/i;

/** The column's temporal kind from its engine data type, or undefined (not temporal / time-of-day). */
function temporalKindOf(dataType: string): TemporalKind | undefined {
  const lower = dataType.toLowerCase().trim();
  // ENUM / SET members and type arguments (`timestamp(6)`) carry no signal.
  const base = lower.replace(/\(.*?\)/g, '').replace(/\s+/g, ' ').trim();
  if (base === 'date') return 'date';
  if (base === 'datetimeoffset' || base === 'timestamptz' || /^timestamp with (local )?time zone$/.test(base)) {
    return 'timestamptz';
  }
  if (base === 'datetime' || base === 'datetime2' || base === 'smalldatetime') return 'timestamp';
  if (base === 'timestamp' || base === 'timestamp without time zone') return 'timestamp';
  return undefined;
}

function parseTemporalValue(value: unknown): ParsedTemporal | undefined {
  const text = value instanceof Date ? (Number.isNaN(value.getTime()) ? '' : value.toISOString()) : value;
  if (typeof text !== 'string') return undefined;
  const m = TEMPORAL_VALUE_RE.exec(text.trim());
  if (!m) return undefined;
  const [, date, hh, mi, ss, fraction, zone] = m;
  const normalizedZone = !zone || zone.toUpperCase() === 'Z'
    ? (zone ? 'Z' : '')
    : zone.length === 3
      ? `${zone}:00`
      : zone.includes(':') ? zone : `${zone.slice(0, 3)}:${zone.slice(3)}`;
  return {
    date: date!,
    time: `${hh ?? '00'}:${mi ?? '00'}:${ss ?? '00'}`,
    fraction: fraction ?? '',
    zone: normalizedZone,
  };
}

function isoText(p: ParsedTemporal, withZone: boolean): string {
  return `${p.date}T${p.time}${p.fraction ? `.${p.fraction}` : ''}${withZone ? p.zone : ''}`;
}

/**
 * The engine's temporal literal for `value` compared with a column of
 * `dataType`, or `undefined` when the policy does not apply (not a temporal
 * column, not a recognisable date / datetime value, or an engine that keeps
 * the plain string) — the caller then uses its ordinary value escaper.
 */
export function sqlTemporalLiteral(
  value: unknown,
  engine: DatabaseEngineId,
  dataType: string | undefined,
): string | undefined {
  if (!dataType) return undefined;
  const kind = temporalKindOf(dataType);
  if (!kind) return undefined;
  const p = parseTemporalValue(value);
  if (!p) return undefined;
  switch (engine) {
    case 'oracle': {
      if (kind === 'date') return `TO_DATE('${p.date} ${p.time}', 'YYYY-MM-DD HH24:MI:SS')`;
      const wallClock = `${p.date} ${p.time}${p.fraction ? `.${p.fraction}` : ''}`;
      if (kind === 'timestamptz' && p.zone) {
        const offset = p.zone === 'Z' ? '+00:00' : p.zone;
        return `TO_TIMESTAMP_TZ('${wallClock} ${offset}', 'YYYY-MM-DD HH24:MI:SS.FF TZH:TZM')`;
      }
      return `TO_TIMESTAMP('${wallClock}', 'YYYY-MM-DD HH24:MI:SS.FF')`;
    }
    case 'mssql':
      // A tz-naive column drops the zone: the UTC-anchored text is its wall
      // clock, `datetime` rejects an offset outright and `datetime2` ignores it.
      return `'${isoText(p, kind === 'timestamptz')}'`;
    case 'pgsql':
    case 'mysql':
    case 'sqlite':
    case 'kusto':
    case 'mock':
      // The engine takes the value as written (see the header).
      return undefined;
  }
}

const ORACLE_TEMPORAL_LITERAL_RE =
  /^TO_(?:DATE|TIMESTAMP|TIMESTAMP_TZ)\(\s*'(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?(?: ([+-]\d{2}:\d{2}))?'\s*,\s*'YYYY-MM-DD HH24:MI:SS(?:\.FF)?(?: TZH:TZM)?'\s*\)$/i;

/**
 * Inverse of the Oracle branch of `sqlTemporalLiteral`: the date-input value
 * (`YYYY-MM-DDThh:mm:ss[.fff][±hh:mm]`) inside an emitted `TO_DATE` /
 * `TO_TIMESTAMP` / `TO_TIMESTAMP_TZ` call, or `null` for any other text. The
 * other engines emit a plain quoted string, which the predicate parser
 * already reads back.
 */
export function parseSqlTemporalLiteral(raw: string): string | null {
  const m = ORACLE_TEMPORAL_LITERAL_RE.exec(raw.trim());
  if (!m) return null;
  const [, date, time, fraction, offset] = m;
  return `${date}T${time}${fraction ? `.${fraction}` : ''}${offset ?? ''}`;
}
